import React from 'react';
import Zousan from 'zousan';

import { Button } from '@ui/buttons/Button.tsx';
import { isAppleMobile } from '@utils/dom';
import { calcOnce } from '@utils/funcs.ts';
import { distance } from '@utils/geodesy.ts';
import { StateObjMultiple } from '@utils/stateObj.ts';

import { widgetEnum } from '../../../../utils/constants.ts';
import type { PluginConfig } from '../config-schema.ts';
import createMarker from './bluedotMarker.tsx';
import FollowMeButton from './FollowMeButton.tsx';
import { createLocationStatusReporter } from './locationStatus.ts';
import MapShiftClickLocationProvider from './MapShiftClickLocationProvider.ts';
import SimLocationProvider, { initDebugTools } from './SimLocationProvider.ts';
import WebGeoLocationProvider from './WebGeoLocationProvider.ts';

// How long to wait for a blue dot before suggesting the user check their location settings
const NO_BLUEDOT_TIMEOUT_MS = 15000;
const LOCATION_HELP_URL = 'https://support.apple.com/en-us/102647';
const PERMISSION_DENIED = 1; // GeolocationPositionError.PERMISSION_DENIED (site blocked, or Location Services off)

// True if a fix's accuracy circle overlaps the bounds. Approximate fixes are deliberately
// offset by the OS, so we can't require the point itself to be inside the venue.
const isNearBounds = (latitude, longitude, accuracy, { n, s, e, w }) => {
  const clampedLat = Math.min(Math.max(latitude, s), n);
  const clampedLng = Math.min(Math.max(longitude, w), e);
  return distance(latitude, longitude, clampedLat, clampedLng) <= accuracy;
};

async function create(app, config: PluginConfig & { forcebluedot?: boolean }) {
  let shiftClickPressedOnce = false; // Used in testing, to allow the follow me button to be shown on desktop when forcebluedot is enabled, once shift-click has been used to set a location at least once.

  const widgetState = new StateObjMultiple({
    isFollowing: false,
    isMonitoring: false,
    hasLocation: false,
  });

  const log = app.log.sublog('bluedot');

  // begin by defaulting to no support - once venue data is loaded, we will check if it has clFloor info (Core Location)
  let venueSupportsBluedot = false; // Once venue data is loaded, set this appropriately
  const deviceSupportsBluedot = navigator.geolocation && isAppleMobile(); // currently only support devices that support CoreLocation

  let locationProvider;
  let marker;

  // setup a new promise to resolve once we enter the init lifecycle
  const initLifecycle = new Zousan();

  const reportLocationStatus = createLocationStatusReporter(app);

  let noBluedotTimer = null;

  const showLocationToast = (messageKey: string, toastType: 'allow' | 'precise') => {
    if (!(config.notifyIfDisabled ?? true)) {
      return;
    }
    const T = app.gt();
    app.bus.send('appInsights/log', { name: 'bluedot/locationToast', properties: { toast: toastType } });
    app.bus.send('toast/show', {
      variant: 'info',
      message: T(messageKey),
      actions: (
        <Button
          type="button"
          fontColor="#FFFFFF"
          style={{ textDecoration: 'underline', fontWeight: 600, whiteSpace: 'nowrap' }}
          onClick={() => window.open(LOCATION_HELP_URL, '_blank', 'noopener')}
        >
          {T('bluedot:locationToastButton')}
        </Button>
      ),
    });
  };

  // Each shown at most once per session - don't keep bugging user
  const showAllowLocationToast = calcOnce(() => showLocationToast('bluedot:allowLocationToast', 'allow'));
  const showPreciseLocationToast = calcOnce(() => showLocationToast('bluedot:preciseLocationToast', 'precise'));

  const onLocationError = (err?: { code?: number }) => {
    if (err?.code === PERMISSION_DENIED) {
      reportLocationStatus('disabled');
      showAllowLocationToast();
    } else {
      reportLocationStatus('error', { errorCode: err?.code });
    }
  };

  // If there's still no blue dot once the timer fires, but the user appears to be at the venue,
  // their location is likely approximate (Precise Location off) or has no floor.
  const startNoBluedotTimer = venueBounds => {
    clearTimeout(noBluedotTimer);
    noBluedotTimer = setTimeout(() => {
      const { hasLocation, latitude, longitude, accuracy } = widgetState.getState();
      if (!hasLocation && latitude != null && isNearBounds(latitude, longitude, accuracy, venueBounds)) {
        showPreciseLocationToast();
      }
    }, NO_BLUEDOT_TIMEOUT_MS);
  };

  const toggleIsFollowing = () => {
    widgetState.update({ isFollowing: !widgetState.getState().isFollowing });
    if (marker) {
      marker.refresh();
    }
  };

  // when venue data loads, make note of the bounds and structures - if a new venue is loaded, this will udpate as well
  // Note: this is only run from within the init() lifecycle method, which is important. It is defined out here to
  // facilitate testing.
  const mapLoadedListener = async ({ structures, venueBounds }) => {
    venueSupportsBluedot = await app.bus
      .get('venueData/getPositioningSupported')
      .then(ps => ps && ps.includes && ps.includes('CoreLocation'));

    const isMonitoring = Boolean((venueSupportsBluedot && deviceSupportsBluedot) || config.forcebluedot);
    widgetState.update({ isFollowing: isMonitoring, isMonitoring });

    if (!isMonitoring) {
      reportLocationStatus('skipped', { reason: deviceSupportsBluedot ? 'venue' : 'device' });
      clearTimeout(noBluedotTimer);
      return;
    }

    marker = createMarker(app, log, structures, venueBounds, widgetState, reportLocationStatus);
    try {
      if (locationProvider) {
        locationProvider.stop();
      }
      locationProvider = new WebGeoLocationProvider();
      reportLocationStatus('requested');
      startNoBluedotTimer(venueBounds);
      locationProvider.start(marker.updatePos, onLocationError);
    } catch (e) {
      clearTimeout(noBluedotTimer);
      log.error(e);
      log.info('Stopping all bluedot activity');
      widgetState.update({
        isFollowing: false,
        isMonitoring: false,
        hasLocation: false,
      });
    }

    // When testing bluedot,  launch the shift-click location provider by default if the user is not on a mobile device (since geolocation doesn't work on desktop)
    if (!app.env.isMobile() && config.forcebluedot) {
      setTimeout(launchShiftClick, 5000); // delay a bit to allow map to load and user to see instructions in the log about shift-clicking to set location
    }
  };

  // protect the listener with an initLifecycle promise to ensure we wait for the init phase
  app.bus.on('venueData/mapDataLoaded', data => {
    initLifecycle.then(() => mapLoadedListener(data));
  });

  async function init() {
    initLifecycle.resolve(true); // we are now free to send messages on the bus and expect data back

    app.bus.send('layers/register', {
      id: 'bluedot/followMeButton',
      widget: () =>
        config.forcebluedot || (venueSupportsBluedot && deviceSupportsBluedot) ? (
          <FollowMeButton widgetState={widgetState} onClick={toggleIsFollowing} />
        ) : null,
      layoutId: app.env.isDesktop() ? 'buttonOptions' : 'mapControls',
      isOverlay: true,
      widgetType: config.forcebluedot ? undefined : widgetEnum.Mobile, // when testing we want to be able to see it on desktop too for testing
      shouldShow: currentlyShowing => {
        // TODO: Remove this line, we want to be able to see it on desktop too for testing
        if (config.forcebluedot && app.env.isDesktop()) {
          return shiftClickPressedOnce;
        }
        if (app.config.uiHide && app.config.uiHide.controls) {
          return false;
        }
        if (currentlyShowing.includes('SearchResultsBody') && currentlyShowing.includes('SearchResultsHeader')) {
          return true;
        }
        const widgetsToCheck = [
          'SearchResultsHeader',
          'DirectionsSearchControls',
          'DirectionsResultControls',
          'DirectionsResultMobile',
          'poiViewMobile',
          'SearchResultsCategoryList',
        ];
        return !widgetsToCheck.some(widget => currentlyShowing.includes(widget));
      },
      order: 5,
    });
  }

  const stopFollowing = () => {
    if (widgetState.getState().isFollowing) {
      widgetState.update({ isFollowing: false });
      if (marker) {
        marker.refresh();
      }
    }
  };

  app.bus.on('mapLevelSelector/selectLevel', stopFollowing);
  app.bus.on('bluedot/stopFollowing', stopFollowing);

  app.bus.on('map/userMoveStart', () => {
    /*
    NOTE: had to abort use of onInteractionStateChange - it wasn't setting these variables (so state is undefined) - maybe some day.
      interactionState.inTransition (Boolean)
      interactionState.isDragging (Boolean)
      interactionState.isPanning (Boolean)
      interactionState.isRotating (Boolean)
      interactionState.isZooming (Boolean)
    */
    // if (state.isPanning || state.isZooming)
    stopFollowing();
  });
  app.bus.monitor('navigation/showDirectionsFromTo', ({ endpointIds: [from] }) => {
    if (!from || !from.isPhysicalLocation) {
      stopFollowing();
    }
  });

  // items seperated by :
  // lat:lng:clFloor:accuracy (last 1 optional - clFloor if undefined mimics not inside or floor not known)
  const geo = term => {
    // i.e. 37.783926:-122.407130:1
    const fields = term.split(':');
    const lat = parseFloat(fields[0]);
    const lng = parseFloat(fields[1]);
    const clFloor = fields.length > 2 ? parseFloat(fields[2]) : undefined;
    const acc = fields.length > 3 ? parseInt(fields[3]) : 30; // default to 30 meters
    log.info(`manual bluedot at ${lat},${lng} on floor ${clFloor} with accuracy ${acc}`);
    marker.updatePos(Date.now(), lat, lng, acc, clFloor, 'Simulator');
  };

  const launchSim = (script, speed) => {
    locationProvider.stop();
    locationProvider = new SimLocationProvider(script, speed);
    locationProvider.start(marker.updatePos);
  };

  const launchShiftClick = () => {
    if (locationProvider && locationProvider.stop) {
      locationProvider.stop();
    }
    locationProvider = new MapShiftClickLocationProvider(app);
    locationProvider.start((...args) => {
      marker.updatePos(...args);
      shiftClickPressedOnce = true;
      app.bus.send('layers/show', { id: 'bluedot/followMeButton' }); // ensure follow me button is shown once shift-click has been used to set location, since on desktop we don't show it by default since geolocation doesn't work on desktop
    });
    log.info('Shift-click location provider active - shift-click on the map to set bluedot position');
  };

  app.bus.on('bluedot/geo', ({ term }) => geo(term));
  app.bus.on('bluedot/sim', ({ script, speed }) => launchSim(script, speed));
  app.bus.on('bluedot/shiftClick', () => launchShiftClick());

  if (app.debug) {
    initDebugTools(app);
    window.geo = geo;
    window.sim = launchSim;
    window.shiftClick = launchShiftClick;
    window.blueState = widgetState;
  }

  /**
   * @return {Endpoint} - user location endpoint
   */
  const returnPhysicalLocation = async () => {
    const T = app.gt();
    if (marker) {
      const lastReading = marker.getLastReading();
      if (lastReading && widgetState.getState().hasLocation) {
        const { latitude, longitude, ordinal, structureId } = lastReading;
        const floorId = await getFloorId(lastReading);
        return {
          title: T('bluedot:My Location'),
          lat: latitude,
          lng: longitude,
          floorId,
          ordinal,
          isPhysicalLocation: true,
          structureId,
          responseOrder: 10,
        };
      }
    }
  };

  app.bus.on('user/getPhysicalLocation', returnPhysicalLocation);

  const floorAtCurrentLocation = location => app.bus.get('map/getFloorAt', location).then(floor => floor && floor.id);
  const floorAtMapCenter = () => app.bus.get('map/getMapCenter').then(center => center.floorId);

  // If the last reading has a floorId attached to it, we simply return that,
  // else, we use the floor at our current reading - or failing that, just the floor at the
  // map center (which could be null if map center is not within a building)
  const getFloorId = async lastReading =>
    Promise.resolve(lastReading.floorId)
      .then(floorId => floorId || floorAtCurrentLocation())
      .then(floorId => floorId || floorAtMapCenter());

  app.bus.on('homeview/performConfirmedSearch', ({ term }) => {
    term = term.toLowerCase();

    if (term === 'debug:bluedotsim') {
      launchSim();
    }

    if (term === 'debug:bluedotsimstop') {
      if (locationProvider?.stop) {
        locationProvider.stop();
      }
      locationProvider = new WebGeoLocationProvider();
      locationProvider.start(marker?.updatePos);
    }
    if (term === 'debug:shiftclick') {
      launchShiftClick();
    }
    if (term.startsWith('debug:geo:')) {
      geo(term.substring(10));
    }
  });

  return {
    getState: widgetState.getState,
    init,
    internal: {
      getFloorId,
      mapLoadedListener,
      returnPhysicalLocation,
    },
  };
}

export { create };
